import { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import type { TableMeta } from '../services/api';
import './ExportPanel.css';

interface Props { fileId: string; tables: TableMeta[]; }

interface QueryStateItem {
  query_id: string;
  query_text: string;
  stage: string;
  created_at: number;
}

export default function ExportPanel({ fileId, tables }: Props) {
  const api = useApi();
  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([]);
  const [tableFormat, setTableFormat] = useState<'xlsx' | 'csv' | 'markdown'>('xlsx');
  const [queryStates, setQueryStates] = useState<QueryStateItem[]>([]);
  const [selectedQueryId, setSelectedQueryId] = useState('');
  const [queryFormat, setQueryFormat] = useState<'xlsx' | 'csv' | 'markdown' | 'json'>('xlsx');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // load sessions, then find completed queries in parallel
    api.get('/sessions').then(async r => {
      const sessions = r.data.sessions || [];
      const results = await Promise.allSettled(
        sessions.map((s: any) => api.get(`/state/session/${s.session_id}/queries`))
      );
      const allQueries: QueryStateItem[] = [];
      for (const res of results) {
        if (res.status === 'fulfilled') {
          const done = (res.value.data.queries || []).filter((q: QueryStateItem) =>
            q.stage === 'complete' || q.stage === 'sample_done'
          );
          allQueries.push(...done);
        }
      }
      setQueryStates(allQueries);
    }).catch(() => {});
  }, [api, fileId]);

  const toggleTable = (id: string) => {
    setSelectedTableIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportTable = async (tableId: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/export/table', { table_id: tableId, format: tableFormat }, { responseType: 'blob' });
      const ext = tableFormat;
      downloadBlob(res.data, `table_${tableId.slice(0, 8)}.${ext}`);
      setSuccess(`Exported as .${ext}`);
    } catch {
      setError('Export failed');
    } finally {
      setLoading(false);
    }
  };

  const exportAllTables = async () => {
    const allIds = tables.map(t => t.table_id);
    setSelectedTableIds(allIds);
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/export/multi-table', { table_ids: allIds }, { responseType: 'blob' });
      downloadBlob(res.data, 'export_all.xlsx');
      setSuccess('All tables exported');
    } catch {
      setError('Export failed');
    } finally {
      setLoading(false);
    }
  };

  const exportMulti = async () => {
    if (selectedTableIds.length === 0) { setError('Select at least one table'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/export/multi-table', { table_ids: selectedTableIds }, { responseType: 'blob' });
      downloadBlob(res.data, 'export_multi.xlsx');
      setSuccess('Multi-table export downloaded');
    } catch {
      setError('Export failed');
    } finally {
      setLoading(false);
    }
  };

  const exportQuery = async () => {
    if (!selectedQueryId) { setError('Select a query result'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/export/query-result', { query_id: selectedQueryId, format: queryFormat }, { responseType: 'blob' });
      downloadBlob(res.data, `result_${selectedQueryId.slice(0, 8)}.${queryFormat}`);
      setSuccess(`Query result exported as .${queryFormat}`);
    } catch {
      setError('Export failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="export-panel">
      {error && <div className="export-msg error">{error} <button onClick={() => setError('')}>✕</button></div>}
      {success && <div className="export-msg success">{success} <button onClick={() => setSuccess('')}>✕</button></div>}

      {/* Export all tables one-click */}
      {tables.length > 1 && (
        <div className="export-section card">
          <h3 className="export-section-title">⚡ Quick Export All</h3>
          <button className="btn btn-primary" onClick={exportAllTables} disabled={loading}>
            ↓ Export all {tables.length} tables as single .xlsx
          </button>
        </div>
      )}

      {/* Export individual table */}
      <div className="export-section card">
        <h3 className="export-section-title">📄 Export Table</h3>
        <div className="export-table-list">
          {tables.map(t => (
            <div key={t.table_id} className="export-table-row">
              <span className="export-table-loc">{t.start_col}{t.start_row}:{t.end_col}{t.end_row}</span>
              <span className="export-table-info">{t.row_count} rows · {t.headers.slice(0, 3).join(', ')}{t.headers.length > 3 ? '…' : ''}</span>
              <select
                className="export-fmt-select"
                value={tableFormat}
                onChange={e => setTableFormat(e.target.value as any)}
              >
                <option value="xlsx">.xlsx</option>
                <option value="csv">.csv</option>
                <option value="markdown">.md</option>
              </select>
              <button className="btn btn-secondary export-btn" onClick={() => exportTable(t.table_id)} disabled={loading}>
                ↓ Export
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Export multiple tables as one workbook */}
      <div className="export-section card">
        <h3 className="export-section-title">📦 Export Multiple Tables (single .xlsx)</h3>
        <div className="export-table-list">
          {tables.map(t => (
            <label key={t.table_id} className="export-check-row">
              <input
                type="checkbox"
                checked={selectedTableIds.includes(t.table_id)}
                onChange={() => toggleTable(t.table_id)}
              />
              <span>{t.start_col}{t.start_row}:{t.end_col}{t.end_row} — {t.headers.slice(0, 3).join(', ')}{t.headers.length > 3 ? '…' : ''} ({t.row_count} rows)</span>
            </label>
          ))}
        </div>
        <button className="btn btn-primary" onClick={exportMulti} disabled={loading || selectedTableIds.length === 0}>
          ↓ Export {selectedTableIds.length > 0 ? `${selectedTableIds.length} tables` : 'selected tables'}
        </button>
      </div>

      {/* Export query result */}
      <div className="export-section card">
        <h3 className="export-section-title">💬 Export Query Result</h3>
        {queryStates.length === 0 ? (
          <p className="export-empty">No completed queries yet. Run a query in the Chat tab first.</p>
        ) : (
          <>
            <div className="export-row">
              <select value={selectedQueryId} onChange={e => setSelectedQueryId(e.target.value)} style={{ flex: 1 }}>
                <option value="">Select a query...</option>
                {queryStates.map(q => (
                  <option key={q.query_id} value={q.query_id}>
                    {q.query_text?.slice(0, 60)} ({q.stage})
                  </option>
                ))}
              </select>
              <select value={queryFormat} onChange={e => setQueryFormat(e.target.value as any)}>
                <option value="xlsx">.xlsx</option>
                <option value="csv">.csv</option>
                <option value="markdown">.md</option>
                <option value="json">.json</option>
              </select>
              <button className="btn btn-primary" onClick={exportQuery} disabled={loading || !selectedQueryId}>
                ↓ Export
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
